import { useEffect, useRef, useState } from "react";

import type {
  ChatView,
  MediaPresentation,
  OfferTimelineItem,
  TimelineItem,
} from "../api/contracts";
import { isOfferPurchased } from "../state/workspace";

export function Timeline({
  actionsDisabled,
  chat,
  loadingEarlier,
  onLoadEarlier,
  onPurchase,
  pendingOfferId,
}: {
  actionsDisabled: boolean;
  chat: ChatView;
  loadingEarlier: boolean;
  onLoadEarlier: () => Promise<void>;
  onPurchase: (offerId: string) => Promise<void>;
  pendingOfferId: string | null;
}) {
  const endRef = useRef<HTMLDivElement>(null);
  const lastItemId = chat.timeline.at(-1)?.itemId;

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [chat.chatId, lastItemId]);

  return (
    <div className="timeline" aria-label={`Conversation with fan ${chat.fanId}`} aria-live="polite">
      {chat.timelineNextBefore !== null ? (
        <button
          className="history-page-button history-page-button--timeline"
          type="button"
          disabled={loadingEarlier}
          onClick={() => void onLoadEarlier()}
        >
          {loadingEarlier ? "Loading…" : "Load earlier messages"}
        </button>
      ) : null}
      <div className="timeline-start">
        <span>Conversation started</span>
        <time dateTime={chat.createdAt}>{formatTimestamp(chat.createdAt)}</time>
      </div>
      {chat.timeline.map((item) => (
        <TimelineEntry
          item={item}
          key={item.itemId}
          onPurchase={onPurchase}
          pendingOfferId={pendingOfferId}
          purchasePending={actionsDisabled || pendingOfferId !== null}
          purchased={item.kind === "offer.v1" && isOfferPurchased(chat.timeline, item.offerId)}
        />
      ))}
      <div ref={endRef} />
    </div>
  );
}

function TimelineEntry({
  item,
  onPurchase,
  pendingOfferId,
  purchasePending,
  purchased,
}: {
  item: TimelineItem;
  onPurchase: (offerId: string) => Promise<void>;
  pendingOfferId: string | null;
  purchasePending: boolean;
  purchased: boolean;
}) {
  switch (item.kind) {
    case "fan_message.v1":
      return <MessageBubble direction="fan" item={item} text={item.text} />;
    case "model_message.v1":
      return <MessageBubble direction="model" item={item} text={item.text} />;
    case "media.v1":
      return (
        <Bubble direction="model" item={item} timestamp={item.timestamp}>
          <MediaCard media={item} />
        </Bubble>
      );
    case "offer.v1":
      return (
        <OfferCard
          item={item}
          purchased={purchased}
          pending={pendingOfferId === item.offerId}
          purchasePending={purchasePending}
          onPurchase={onPurchase}
        />
      );
    case "purchase.v1":
      return (
        <div
          className="timeline-event timeline-event--purchase"
          data-item-id={item.itemId}
          data-timeline-kind={item.kind}
        >
          <span>Purchase confirmed</span>
          <time dateTime={item.timestamp}>{formatTimestamp(item.timestamp)}</time>
        </div>
      );
    case "status.v1":
      return (
        <div
          className={`timeline-event timeline-event--${item.stage}`}
          data-item-id={item.itemId}
          data-stage={item.stage}
          data-timeline-kind={item.kind}
        >
          <span>{item.message ?? item.stage.replaceAll("_", " ")}</span>
          <time dateTime={item.timestamp}>{formatTimestamp(item.timestamp)}</time>
        </div>
      );
  }
}

function MessageBubble({
  direction,
  item,
  text,
}: {
  direction: "fan" | "model";
  item: TimelineItem;
  text: string;
}) {
  return (
    <Bubble direction={direction} item={item} timestamp={item.timestamp}>
      <p className="message-text">{text}</p>
    </Bubble>
  );
}

function Bubble({
  children,
  direction,
  item,
  timestamp,
}: {
  children: React.ReactNode;
  direction: "fan" | "model";
  item: TimelineItem;
  timestamp: string;
}) {
  return (
    <article
      className={`bubble-row bubble-row--${direction}`}
      data-item-id={item.itemId}
      data-timeline-kind={item.kind}
    >
      <div className={`bubble bubble--${direction}`}>
        {children}
        <time dateTime={timestamp}>{formatTimestamp(timestamp)}</time>
      </div>
    </article>
  );
}

function OfferCard({
  item,
  onPurchase,
  pending,
  purchasePending,
  purchased,
}: {
  item: OfferTimelineItem;
  onPurchase: (offerId: string) => Promise<void>;
  pending: boolean;
  purchasePending: boolean;
  purchased: boolean;
}) {
  return (
    <Bubble direction="model" item={item} timestamp={item.timestamp}>
      <div className="offer-card">
        {item.media ? <MediaCard media={item.media} /> : <div className="offer-placeholder">Paid media</div>}
        <div className="offer-meta">
          <div>
            <span className="offer-label">Demo purchase</span>
            <strong>{item.price} {item.currency}</strong>
          </div>
          <button
            className={purchased ? "purchase-button purchase-button--done" : "purchase-button"}
            data-offer-id={item.offerId}
            type="button"
            disabled={purchased || purchasePending}
            onClick={() => void onPurchase(item.offerId)}
          >
            {purchased ? "Purchased" : pending ? "Purchasing…" : "Purchase"}
          </button>
        </div>
      </div>
    </Bubble>
  );
}

function MediaCard({ media }: { media: MediaPresentation }) {
  const [mediaRevealed, setMediaRevealed] = useState(false);

  if (media.presentation === "photo") {
    return (
      <a
        aria-label={mediaRevealed ? "Blur photo" : "Unblur photo"}
        aria-pressed={mediaRevealed}
        className={`media-card media-card--photo${mediaRevealed ? " media-card--revealed" : ""}`}
        data-media-id={media.mediaId}
        data-media-presentation={media.presentation}
        href={media.mediaUrl}
        onClick={(event) => {
          event.preventDefault();
          setMediaRevealed((revealed) => !revealed);
        }}
        role="button"
        target="_blank"
        rel="noreferrer"
      >
        <img src={media.mediaUrl} alt={media.alt} loading="lazy" />
        {!mediaRevealed ? (
          <span className="media-card__nsfw-label" aria-hidden="true">
            NSFW — press to unblur
          </span>
        ) : null}
      </a>
    );
  }

  return (
    <button
      aria-label={mediaRevealed ? "Blur video preview" : "Unblur video preview"}
      aria-pressed={mediaRevealed}
      className={`media-card media-card--video-preview${mediaRevealed ? " media-card--revealed" : ""}`}
      data-media-id={media.mediaId}
      data-media-presentation={media.presentation}
      onClick={() => setMediaRevealed((revealed) => !revealed)}
      type="button"
    >
      <img src={media.mediaUrl} alt="" loading="lazy" />
      {mediaRevealed ? <span className="video-preview-triangle" aria-hidden="true" /> : null}
      {!mediaRevealed ? (
        <span className="media-card__nsfw-label" aria-hidden="true">
          NSFW — press to unblur
        </span>
      ) : null}
    </button>
  );
}

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(new Date(value));
}
